<template>
  <div
    :class="`${expanded ? 'w-11/12 absolute' : 'w-full sm:w-1/4'} min-w-64 mt-5 mr-5 py-2 pl-5 pr-3 pt-1 rounded-md shadow-md transition-all duration-500 max-h-screen`"
  >
    <div
      class="flex justify-between mt-3"
    >
      <div class="w-full">
        <div
          v-if="special && title"
          class="text-lg font-bold mb-2"
        >
          {{ title }}
        </div>
        <div
          v-else-if="populationTags"
          class="mb-2 flex justify-start flex-wrap w-full"
        >
          <div
            v-for="tag in populationTags"
            :key="tag"
            class="text-purple-500 text-lg mr-2 font-bold break-all"
          >
            #{{ tag }}
          </div>
        </div>
        <div>
          Miembros: <span class="font-bold">{{ personCount }}</span>
        </div>
        <div v-if="!['symptomatic', 'noReports', 'vaccinated', 'unvaccinated', 'wontGetVaccinated'].includes(populationID)">
          Reportes: <span class="font-bold">{{ reportedPercentage }}%</span>
        </div>
        <div
          v-if="!['symptomatic', 'noReports', 'vaccinated', 'unvaccinated', 'wontGetVaccinated'].includes(populationID)"
          class="flex justify-between mt-6"
        >
          <div
            class="flex justify-center w-full"
          >
            <div
              v-if="symptomaticPercentage"
              :style="`width: ${symptomaticPercentage}%`"
              class="flex flex-col text-center text-xs text-red-500"
            >
              <div
                class="h-1 bg-red-500"
              />
              <div class="font-bold whitespace-no-wrap">
                ({{ symptomaticCount }}) {{ symptomaticPercentage }}%
              </div>
            </div>
            <div
              v-if="asymptomaticPercentage"
              :style="`width: ${asymptomaticPercentage}%`"
              class="flex flex-col text-center text-xs text-green-500"
            >
              <div
                class="h-1 bg-green-500"
              />
              <div
                :class="`${getMargin()} font-bold whitespace-no-wrap`"
                style="margin-top: -24px"
              >
                ({{ asymptomaticCount }}) {{ asymptomaticPercentage }}%
              </div>
            </div>
            <div
              class="h-1 bg-bluegray-500"
              :style="`width: ${100 - (asymptomaticPercentage + symptomaticPercentage)}%`"
            />
          </div>
        </div>
      </div>
      <a
        v-if="!special && !expanded"
        @click="deletePopulation(populationID)"
      >
        <eva-icon
          name="minus-circle"
          fill="#FF7570"
        />
      </a>
      <div class="flex flex-col">
        <a
          class="ml-2"
          @click="expanded = !expanded"
        >
          <eva-icon
            :name="!expanded ? 'expand-outline' : 'collapse-outline'"
            fill="#6C63FF"
          />
        </a>
        <a
          v-if="expanded"
          class=" mt-2 ml-2 pt-1 px-1 bg-purple-500 rounded"
          @click="exportPopulation"
        >
          <eva-icon
            name="cloud-download-outline"
            fill="#FFFFFF"
          />
        </a>
      </div>
    </div>
    <population-details
      v-if="showDetails"
      :populationID="populationID"
      :dateTimestamp="today || population.today"
      :special="special"
      :personIDs="population.personIDs"
      @close="expanded = false"
    />
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import PopulationDetails from './populationDetails'
import moment from 'moment-timezone'
import * as axios from 'axios'
import XLSX from 'xlsx'
import { saveAs } from 'file-saver'

export default {
  name: 'PopulationCard',
  components: {
    PopulationDetails,
  },
  props: {
    special: { type: Boolean, default: false },
    title: { type: String, default: '' },
    populationID: { type: String, default: '' },
    today: { type: Date, default: () => {} },
    population: {
      default: function () {
        return { }
      },
      type: Object,
    },
  },
  data () {
    return {
      startOfDay: moment.tz('America/Bogota').startOf('day').toDate(),
      expanded: false,
      showDetails: false,
      historyBinding: null,
      historyData: null,
    }
  },
  computed: {
    categories () {
      return this.instancesInfo[this.instanceID].dictionary.legacyForm.categories
    },
    symptomsDictionary () {
      const labels = {}
      for (const categoryID of Object.keys(this.categories)) {
        for (const fieldID of this.categories[categoryID].fieldIDs) {
          labels[fieldID] = this.categories[categoryID].fields[fieldID].label
        }
      }
      return labels
    },
    personCount () {
      if (this.special) return this.population.personCount || 0
      if (!this.populationHistory[this.populationID]) return this.population.personCount || this.population.personIDs?.length || 0
      return this.populationHistory[this.populationID].personCount || this.populationHistory[this.populationID].personIDs?.length || 0
    },
    symptomaticCount () {
      if (!this.population) return 0
      if (this.special) return this.population.symptomaticCount || 0
      if (!this.populationHistory[this.populationID]) return 0
      return (this.populationHistory[this.populationID].symptomaticCount || this.populationHistory[this.populationID].symptomatic?.length || 0)
    },
    reportedCount () {
      if (!this.population) return 0
      if (this.special) return this.population.reportedCount || 0
      if (!this.populationHistory[this.populationID]) return 0
      return (this.populationHistory[this.populationID].reportedCount || this.populationHistory[this.populationID].reported?.length || 0)
    },
    asymptomaticCount () {
      return this.reportedCount - this.symptomaticCount
    },
    reportedPercentage () {
      if (this.personCount === 0) return 0
      return Math.round(this.reportedCount / this.personCount * 100)
    },
    symptomaticPercentage () {
      if (this.personCount === 0) return 0
      return Math.round(this.symptomaticCount / this.personCount * 100)
    },
    asymptomaticPercentage () {
      if (this.personCount === 0) return 0
      return Math.round(this.asymptomaticCount / this.personCount * 100)
    },
    populationTags () {
      const tagNames = []
      for (const tagID in this.population.tagIDs) {
        tagNames.push(this.instancesInfo[this.instanceID].tags[tagID])
      }
      return tagNames.sort()
    },
    ...mapState({
      persons: state => state.admin.persons,
      instancesInfo: state => state.auth.instancesInfo,
      instanceID: state => state.admin.instanceID,
      populations: state => state.admin.populations,
      populationHistory: state => state.admin.populationHistory,
      lastDaysSymptoms: state => state.admin.lastDaysSymptoms,
    }),
    ...mapGetters({
      tags: 'admin/tags',
    }),
  },
  watch: {
    expanded: function (value) {
      if (value) {
        setTimeout(() => {
          this.showDetails = true
        }, 500)
        this.$emit('expanded', this.populationID)
      } else {
        this.showDetails = false
        this.$emit('expanded', null)
      }
    },
  },
  methods: {
    exportPopulation () {
      try {
        const todayDate = moment(this.population.today).tz('America/Bogota').startOf('day').format('DD/MM/YY')
        const wb = XLSX.utils.book_new()
        const csvTitle = this.title || Object.values(this.populationTags).join(',')
        const name = `Cuidarte-${todayDate}-${csvTitle}.xlsx`
        const dates = Object.keys(this.lastDaysSymptoms).sort().map(timestamp => {
          return moment(Number(timestamp)).tz('America/Bogota').startOf('day').format('DD/MM/YY')
        })
        const complete = [
          ['Nombre', 'Celular', 'ID', 'Vacuna 1', 'Vacuna 2', 'Vacuna R', 'email', 'Etiquetas', ...dates],
          ...this.persons.filter(person => {
            return this.population.personIDs.includes(person.id)
          }).map(person => {
            const riskyPersons = {}
            const reported = {}
            const parsedSymptoms = {}
            const tags = person.tagIDs ? Object.keys(person.tagIDs).map(tagID => { return this.tags[tagID] }).sort().join(', ') : ''
            for (const symptomsDay in this.lastDaysSymptoms) {
              if (this.lastDaysSymptoms[symptomsDay].riskyPersons) {
                riskyPersons[symptomsDay] = this.lastDaysSymptoms[symptomsDay].riskyPersons
              } else if (this.lastDaysSymptoms[symptomsDay].symptoms) {
                riskyPersons[symptomsDay] = this.lastDaysSymptoms[symptomsDay].symptoms
              } else {
                riskyPersons[symptomsDay] = null
              }
              if (this.lastDaysSymptoms[symptomsDay].reported) {
                reported[symptomsDay] = this.lastDaysSymptoms[symptomsDay].reported
              } else {
                reported[symptomsDay] = null
              }
              parsedSymptoms[symptomsDay] = 'Sin síntomas'
              if (reported[symptomsDay].includes(person.id)) {
                if (riskyPersons[symptomsDay] && riskyPersons[symptomsDay][person.id]) {
                  const answers = riskyPersons[symptomsDay][person.id].answers
                  let parsedAnswers = []
                  if (answers) {
                    Object.keys(answers).forEach(categoryID => {
                      parsedAnswers = [...parsedAnswers, ...answers[categoryID].labels]
                    })
                    parsedSymptoms[symptomsDay] = parsedAnswers.join(', ')
                  } else {
                    parsedSymptoms[symptomsDay] = riskyPersons[symptomsDay][person.id].map(symptomID => {
                      if (typeof this.symptomsDictionary[symptomID] !== 'undefined') {
                        return this.symptomsDictionary[symptomID]
                      } else {
                        return symptomID
                      }
                    }).join(', ')
                  }
                }
              } else {
                parsedSymptoms[symptomsDay] = 'Sin reportar'
              }
            }
            const sortedParsedSymptoms = Object.keys(this.lastDaysSymptoms).sort().map(timestamp => {
              return parsedSymptoms[timestamp]
            })
            const vaccinationDates = typeof person.vaccinationData !== 'undefined' ? person.vaccinationData.answers.map(answer => {
              if (answer.vaccinated) {
                return moment.tz(new Date(answer.date), 'America/Bogota').format('DD/MM/YYYY')
              }
              return answer.reason
            }) : ['', '', '']
            if (vaccinationDates.length < 3) {
              for (let i = 0; i <= 3 - vaccinationDates.length; i++) {
                vaccinationDates.push('')
              }
            }
            return [person.name, person.phoneNumber, person.documentID, ...vaccinationDates, person.email, tags, ...sortedParsedSymptoms]
          })
        ]
        const w1 = XLSX.utils.aoa_to_sheet(complete)
        XLSX.utils.book_append_sheet(wb, w1, csvTitle.substring(0, 28) + (csvTitle.length >= 28 ? '...' : ''))

        const wopts = { bookType: 'xlsx', bookSST: false, type: 'array' }
        const wbout = XLSX.write(wb, wopts)
        const xlsx = new Blob([wbout], { type: 'application/octet-stream' })

        if (typeof window.plugins !== 'undefined') {
          var reader = new FileReader()
          this.$f7.dialog.preloader('Cargando...')
          reader.readAsDataURL(xlsx)
          reader.onloadend = () => {
            this.$f7.dialog.close()
            const base64data = reader.result
            window.plugins.socialsharing.share(
              null,
              null,
              this.generateAttachment(base64data, name),
              null,
              (result) => { console.info('result: ' + result) },
              (result) => { console.info('error: ' + result) }
            )
          }
        } else {
          saveAs(xlsx, name)
        }
      } catch (err) {
        console.info(err)
        this.$f7.dialog.alert('Hubo un problema descargando este informe', 'Error')
      }
    },
    async deletePopulation () {
      try {
        this.$f7.dialog.confirm(`Estas seguro de que deseas eliminar la población con las etiquetas <span class="font-bold text-purple-500">${this.populationTags.map(tag => '#' + tag).join(' ')}</span>`, 'Eliminar Población', async () => {
          this.$f7.preloader.show('Eliminando población...')
          const response = await axios({
            url: '/populations/remove',
            method: 'POST',
            data: {
              populationID: this.populationID,
            },
          })
          this.$f7.preloader.hide()
          console.info(response)
        })
      } catch (err) {
        this.$f7.preloader.hide()
        console.error(err)
      }
    },
    getMargin () {
      if (this.symptomaticPercentage < 20 && this.asymptomaticPercentage < 20) {
        return '-mt-6'
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@import "../css/_brand";
</style>
